const ABSOLUTE_URL_PATTERN = /^https?:\/\//i;
const DOMAIN_PREFIX_PATTERN = /^(?:www\.)?[a-z0-9.-]+\.[a-z]{2,}(?:[/:?#]|$)/i;
const SPECIAL_URL_PATTERN = /^(?:#|mailto:|tel:|sms:|javascript:|data:)/i;
const EMBEDDED_NORDIT_DOMAIN_PATTERN = /^\/(?:www\.)?nordit\.co(?=\/|$)/i;
const MALFORMED_HR_BLOG_PATTERN = /^\/hrblog(?=\/|$)/i;
const MALFORMED_HR_ROOT_SEGMENTS = [
  'blog',
  'kontakt',
  'kontakt-projekt',
  'nasi-radovi',
  'o-nama',
  'politika-privatnosti',
  'procjena-projekta',
  'thank-you',
  'thank-you-project',
  'usluge',
  'uvjeti-i-odredbe',
] as const;

const collapsePathSlashes = (value: string) => value.replace(/\/{2,}/g, '/');

const splitPathSuffix = (value: string) => {
  const match = value.match(/[?#]/);
  if (!match || match.index === undefined) {
    return { pathname: value, suffix: '' };
  }

  return {
    pathname: value.slice(0, match.index),
    suffix: value.slice(match.index),
  };
};

const normalizeBasePath = (pathname: string) => {
  if (!pathname || pathname === '/') {
    return '';
  }

  return collapsePathSlashes(pathname).replace(/\/+$/, '');
};

const stripEmbeddedNorditDomain = (pathname: string) => {
  let normalizedPathname = pathname;

  while (EMBEDDED_NORDIT_DOMAIN_PATTERN.test(normalizedPathname)) {
    normalizedPathname = normalizedPathname.replace(EMBEDDED_NORDIT_DOMAIN_PATTERN, '') || '/';
  }

  return normalizedPathname.startsWith('/') ? normalizedPathname : `/${normalizedPathname}`;
};

const normalizeMalformedHrRootPath = (pathname: string) => {
  if (!pathname.startsWith('/hr') || pathname === '/hr' || pathname.startsWith('/hr/')) {
    return pathname;
  }

  if (MALFORMED_HR_BLOG_PATTERN.test(pathname)) {
    return pathname.replace(MALFORMED_HR_BLOG_PATTERN, '/hr/blog');
  }

  const remainder = pathname.slice(3);
  const matchingSegment = MALFORMED_HR_ROOT_SEGMENTS.find(
    segment => remainder === segment || remainder.startsWith(`${segment}/`),
  );

  if (!matchingSegment) {
    return pathname;
  }

  return `/hr/${remainder}`;
};

const sanitizePathname = (pathname: string) => {
  const withoutEmbeddedDomain = stripEmbeddedNorditDomain(pathname);
  return normalizeMalformedHrRootPath(withoutEmbeddedDomain);
};

const stripKnownDomain = (value: string) => {
  const trimmed = value.trim();

  if (!trimmed) {
    return '';
  }

  if (ABSOLUTE_URL_PATTERN.test(trimmed)) {
    try {
      const url = new URL(trimmed);
      return `${url.pathname}${url.search}${url.hash}`;
    } catch {
      return trimmed;
    }
  }

  if (DOMAIN_PREFIX_PATTERN.test(trimmed)) {
    const domainless = trimmed.replace(/^(?:www\.)?[a-z0-9.-]+\.[a-z]{2,}/i, '');
    return domainless || '/';
  }

  return trimmed;
};

export const normalizePathWithBase = (value: string, basePath = '') => {
  const stripped = stripKnownDomain(value || '/');
  const candidate = stripped || '/';

  if (ABSOLUTE_URL_PATTERN.test(candidate)) {
    return candidate;
  }

  const { pathname, suffix } = splitPathSuffix(candidate);
  const normalizedPathname = sanitizePathname(
    pathname
    ? collapsePathSlashes(pathname.startsWith('/') ? pathname : `/${pathname}`)
    : '/',
  );
  const normalizedBasePath = normalizeBasePath(basePath);

  let pathnameWithBase = normalizedPathname;
  if (normalizedBasePath) {
    const isRootPath = normalizedPathname === '/';
    const alreadyLocalized =
      normalizedPathname === normalizedBasePath ||
      normalizedPathname.startsWith(`${normalizedBasePath}/`) ||
      normalizedPathname.startsWith(`${normalizedBasePath}?`) ||
      normalizedPathname.startsWith(`${normalizedBasePath}#`);

    if (isRootPath) {
      pathnameWithBase = normalizedBasePath;
    } else if (!alreadyLocalized) {
      pathnameWithBase = `${normalizedBasePath}${normalizedPathname}`;
    }
  }

  const finalPathname = collapsePathSlashes(pathnameWithBase);
  return `${finalPathname}${suffix}`;
};

export const buildAbsoluteUrl = (baseUrl: string, value: string) => {
  const fallbackBase = new URL(baseUrl);
  const normalizedValue = normalizePathWithBase(value || '/', fallbackBase.pathname);

  if (ABSOLUTE_URL_PATTERN.test(normalizedValue)) {
    try {
      const absolute = new URL(normalizedValue);
      if (absolute.hostname === fallbackBase.hostname) {
        return `${fallbackBase.origin}${normalizePathWithBase(
          `${absolute.pathname}${absolute.search}${absolute.hash}`,
          fallbackBase.pathname,
        )}`;
      }

      return absolute.toString();
    } catch {
      return `${fallbackBase.origin}${normalizePathWithBase('/', fallbackBase.pathname)}`;
    }
  }

  return `${fallbackBase.origin}${normalizedValue}`;
};

const isSpecialUrl = (value: string) => SPECIAL_URL_PATTERN.test(value.trim());

const isExternalAbsoluteUrl = (value: string) => {
  if (!ABSOLUTE_URL_PATTERN.test(value.trim())) {
    return false;
  }

  try {
    const parsed = new URL(value);
    return !/(^|\.)nordit\.co$/i.test(parsed.hostname);
  } catch {
    return false;
  }
};

const stripLeadingLocaleSegments = (pathname: string) =>
  pathname.replace(/^\/(?:en|hr)(?=\/|$)/i, '') || '/';

export const normalizeLocalizedHref = (
  value: string,
  locale?: string | null,
  options?: { ensureLocalePrefix?: boolean },
) => {
  const trimmed = (value || '').trim();

  if (!trimmed || isSpecialUrl(trimmed) || isExternalAbsoluteUrl(trimmed)) {
    return trimmed;
  }

  const { pathname, suffix } = splitPathSuffix(stripKnownDomain(trimmed) || '/');
  const normalizedPathname = pathname
    ? collapsePathSlashes(pathname.startsWith('/') ? pathname : `/${pathname}`)
    : '/';
  const normalizedLocale = locale?.toLowerCase().startsWith('hr') ? 'hr' : 'en';
  const ensureLocalePrefix = options?.ensureLocalePrefix ?? normalizedLocale === 'hr';

  if (normalizedLocale === 'en') {
    const withoutEnglishPrefix = normalizedPathname.replace(/^\/en(?=\/|$)/i, '') || '/';
    return `${collapsePathSlashes(withoutEnglishPrefix)}${suffix}`;
  }

  const withoutLeadingLocale = stripLeadingLocaleSegments(normalizedPathname);
  const localizedPath = ensureLocalePrefix
    ? withoutLeadingLocale === '/'
      ? '/hr'
      : `/hr${withoutLeadingLocale}`
    : withoutLeadingLocale;
  return `${collapsePathSlashes(localizedPath)}${suffix}`;
};

export const normalizeLocalizedHtml = (
  value: string,
  locale?: string | null,
  options?: { ensureLocalePrefix?: boolean },
) => {
  const trimmed = (value || '').trim();

  if (!trimmed) {
    return trimmed;
  }

  return trimmed.replace(
    /(<a\b[^>]*?\bhref=(['"]))(.*?)(\2[^>]*>)/gi,
    (match, prefix, quote, hrefValue, suffix) => {
      const normalizedHref = normalizeLocalizedHref(hrefValue, locale, options);
      return `${prefix}${normalizedHref}${suffix}`;
    },
  );
};

export const buildAbsoluteLocalizedUrl = (
  baseUrl: string,
  value: string,
  locale?: string | null,
  options?: { ensureLocalePrefix?: boolean },
) => buildAbsoluteUrl(baseUrl, normalizeLocalizedHref(value, locale, options));
